/**
 * Popover Controller
 * Helper Class to position popovers triggered via the native Popover API
 * 
 * The trigger element needs to have a `popovertarget` attribute
 * pointing to the popover element it controls. That element must be a
 * valid popover element via the `popover` attribute and have a `anchor` attribute pointing
 * to the element it should be anchored to.
 */
class PopoverController {

  /**
   * Check if the Popover API is supported
   * @returns {boolean}
   */
  static supportsPopoverApi =
    typeof HTMLElement !== "undefined" &&
    (Object.hasOwn?.(HTMLElement.prototype, "popover") || "showPopover" in HTMLElement.prototype);

  /**
   * Constructor for PopoverController
   * @param {HTMLButtonElement} popoverButton
   * @returns {PopoverController}
   */
  constructor(popoverButton) {
    if (!this.constructor.supportsPopoverApi) {
      console.warn("Popover API is not supported in this environment");
      return;
    }

    /** @type {HTMLButtonElement} */
    this.popoverButton = popoverButton;
    /** @type {HTMLElement|null} */
    this.popover = popoverButton.popoverTargetElement || null;

    const popoverAnchorId =
      this.popover?.getAttribute("anchor") ||
      this.popoverButton.getAttribute("popoveranchor");

    /** @type {HTMLElement|null} */
    this.popoverAnchor = popoverAnchorId
      ? document.getElementById(popoverAnchorId)
      : popoverButton;

    if (!this.popover || !this.popoverAnchor) return;


    /** @type {HTMLElement|null} */
    this.arrowEl = this.popover.querySelector(".popover__arrow");
    if (!this.arrowEl) {
      this.arrowEl = document.createElement("div");
      this.arrowEl.className = "popover__arrow";
      this.arrowEl.setAttribute("aria-hidden", "true");
      this.popover.prepend(this.arrowEl);
    }


    // Helpers for requestAnimationFrame scheduling
    /** @type {number|null} */
    this._requestedAnimationFrameId = null;
    /** @type {Array<Array>} */
    this._listeners = [];
    /** @type {ResizeObserver|null} */
    this._resizeObserver = null;
    /** @type {Function|null} */
    this._beforeToggleHandler = null;


    // Listen to popover beforetoggle events
    this._beforeToggleHandler = (e) => {
      if (e.newState === "open") {
        this.schedulePosition();   // initial positioning
        this.enableLivePositioning();
        this.observeSizeChanges();

        window.requestAnimationFrame(() => {
          this.popover.classList.add("popover--open");
        });
      } else if (e.newState === "closed") {
        // Immediate cleanup
        this.disableLivePositioning();
        this.unobserveSizeChanges();
        this.cancelScheduled();
        
        this.popover.classList.remove("popover--open");
      }
    };
    this.popover.addEventListener("beforetoggle", this._beforeToggleHandler);

    // Add init data attribute
    this.popoverButton.setAttribute("data-popover-initialized", "true");
  }
  /**
   * Triggers the positioning of the popover.
   */
  positionPopover() {
    if (!this.popover || !this.popoverAnchor) return;
    if (!this.popover.matches(":popover-open")) return;

    this.schedulePosition();
  }

  /**
   * Resets the inline positioning styles of the popover.
   * This is useful for returning the popover to its default state.
   */
  resetInlinePositioning() {
    if (!this.popover) return;
    Object.assign(this.popover.style, {
      position: "",
      inset: "",
      top: "",
      left: "",
      right: "",
      bottom: "",
      transform: "",
      visibility: ""
    });
  }

  /**
   * Scheduling positioning via requestAnimationFrame
   * This method ensures that the popover is positioned correctly
   * in the next animation frame.
   */
  schedulePosition() {
    if (!this.popover || !this.popoverAnchor) return;
    if (!this.popover.matches(":popover-open")) return;
    if (this._requestedAnimationFrameId !== null) return; // already scheduled

    this._requestedAnimationFrameId = requestAnimationFrame(() => {
      this._requestedAnimationFrameId = null;
      this._measureAndWrite();
    });
  }

  /**
   * Cancels any scheduled positioning updates.
   */
  cancelScheduled() {
    if (this._requestedAnimationFrameId !== null) {
      cancelAnimationFrame(this._requestedAnimationFrameId);
      this._requestedAnimationFrameId = null;
    }
  }

  /**
   * Live repositioning on scroll/resize events.
   */
  enableLivePositioning() {
    const onScrollOrResize = () => this.schedulePosition();
    
    // Create options objects once and reuse the same references for add/remove
    const scrollOptions = { capture: true, passive: true };
    const resizeOptions = { passive: true };
    
    window.addEventListener("scroll", onScrollOrResize, scrollOptions);
    window.addEventListener("resize", onScrollOrResize, resizeOptions);
    document.addEventListener("scroll", onScrollOrResize, scrollOptions);
    
    this._listeners.push(
      ["scroll", window, onScrollOrResize, scrollOptions],
      ["resize", window, onScrollOrResize, resizeOptions],
      ["scroll", document, onScrollOrResize, scrollOptions]
    );
  }

  /**
   * Disables live positioning on scroll/resize events.
   */
  disableLivePositioning() {
    for (const [type, target, handler, options] of this._listeners) {
      target.removeEventListener(type, handler, options);
    }
    this._listeners = [];
  }

  /**
   * Observes size changes of the popover element.
   * Registers a ResizeObserver to monitor size changes.
   */
  observeSizeChanges() {
    if (!("ResizeObserver" in window) || !this.popover) return;
    this._resizeObserver = new ResizeObserver(() => this.schedulePosition());
    this._resizeObserver.observe(this.popover);
  }

  /**
   * Stops observing size changes of the popover element.
   */
  unobserveSizeChanges() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  }

  /**
   * Measure the position of the popover and write the styles accordingly.
   * This method calculates the position based on the anchor element's position
   * and the viewport size, ensuring the popover is displayed correctly without
   * overflowing the viewport.
   * It handles flipping the popover to the top if there isn't enough space below,
   * and clamps the popover's position to ensure it stays within the viewport bounds.
   * @internal
   */
  _measureAndWrite() {
    if (!this.popover || !this.popoverAnchor) return;
    if (!this.popover.matches(":popover-open")) return;

    // READS
    const anchorRect = this.popoverAnchor.getBoundingClientRect();

    // Make popover temporarily measurable if display:none/0x0
    let restore = null;
    let popRect = this.popover.getBoundingClientRect();
    const computed = getComputedStyle(this.popover);

    if ((popRect.width === 0 && popRect.height === 0) || computed.display === "none") {
      const prev = {
        position: this.popover.style.position,
        visibility: this.popover.style.visibility,
        display: this.popover.style.display
      };
      this.popover.style.visibility = "hidden";
      this.popover.style.display = "block";
      this.popover.style.position = "fixed";
      popRect = this.popover.getBoundingClientRect();
      restore = () => {
        this.popover.style.position = prev.position;
        this.popover.style.visibility = prev.visibility;
        this.popover.style.display = prev.display;
      };
    }

    const { width: pw, height: ph } = popRect;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Target position (numeric values only)
    let top = anchorRect.bottom;
    let left = anchorRect.left;

    // Flip up if there's no space below
    let flippedUp = false;
    if (top + ph > vh && anchorRect.top - ph >= 0) {
      top = anchorRect.top - ph;
      flippedUp = true;
    }

    // Clamp right
    if (left + pw > vw) {
      left = Math.max(8, vw - pw - 8);
    }
    // Clamp left
    if (left < 8) left = 8;

    // WRITES
    Object.assign(this.popover.style, {
      position: "fixed",
      top: `${Math.round(top)}px`,
      left: `${Math.round(left)}px`,
      right: "auto",
      bottom: "auto",
      transform: "none",
      visibility: ""
    });

    // Side (top/bottom) on the popover
    this.popover.dataset.side = flippedUp ? "top" : "bottom";

    // X position of the arrow: center of the anchor relative to the popover left
    // Note: popRect.left is the old value – after setting 'left'
    // we read the current left edge again.
    const updatedPopLeft = this.popover.getBoundingClientRect().left;
    const anchorCenterX = anchorRect.left + anchorRect.width / 2;
    let arrowX = anchorCenterX - updatedPopLeft;

    // clamp within the popover (small margin to prevent the arrow from "breaking")
    const arrowSize = parseFloat(getComputedStyle(this.popover).getPropertyValue("--popover-arrow-size")) || 5;
    const sidePadding = Math.max(8, arrowSize + 4); // 8px padding or arrowSize+4, whichever is larger
    arrowX = Math.max(sidePadding, Math.min(pw - sidePadding, arrowX));

    // Set CSS variable
    this.popover.style.setProperty("--popover-arrow-x", `${Math.round(arrowX)}px`);

    if (restore) restore();
  }

  static isAnyPopoverOpen() {
    return !!document.querySelector('[popover]:popover-open');
  }

  static init() {
    if (!PopoverController.supportsPopoverApi) {
      console.warn("PopoverController: Popover API is not supported in this environment");
      return [];
    }

    const anchors = document.querySelectorAll('[data-js-popover-anchor]');
    const controllers = [];

    anchors.forEach(anchor => {
      try {
        if (!anchor.dataset.popoverInitialized) {
          // Validate that the anchor has the required attributes
          if (!anchor.hasAttribute('popovertarget')) {
            console.warn('PopoverController: Anchor element missing popovertarget attribute', anchor);
            return;
          }

          const controller = new PopoverController(anchor);
          if (controller) {
            controllers.push(controller);
          }
        }
      } catch (error) {
        console.error('PopoverController: Failed to initialize controller for anchor', anchor, error);
      }
    });

    return controllers;
  }
}

window.PopoverController = PopoverController;
